import React, { useState, useEffect } from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X, Loader2, UserPlus } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import api from '../lib/api';
import { cn } from '@/lib/utils';

/**
 * ClientQuickCreate — nested dialog used inside Quotation/Invoice forms.
 * On success calls `onCreated(newClient)` so the parent can auto-select it.
 */
const BLANK = {
  client_name: '',
  company_name: '',
  pic_name: '',
  whatsapp_number: '',
  phone_number: '',
  email: '',
  address: '',
  npwp: '',
  notes: '',
};

export const ClientQuickCreate = ({ open, onOpenChange, onCreated, existingClients = [] }) => {
  const [form, setForm] = useState(BLANK);
  const [saving, setSaving] = useState(false);
  const [warn, setWarn] = useState('');

  useEffect(() => { if (open) { setForm(BLANK); setWarn(''); } }, [open]);

  // Live duplicate warning (not blocking)
  useEffect(() => {
    const name = (form.client_name || '').trim().toLowerCase();
    const company = (form.company_name || '').trim().toLowerCase();
    if (!name) { setWarn(''); return; }
    const dup = existingClients.find(c =>
      (c.client_name || '').toLowerCase() === name &&
      ((c.company_name || '').toLowerCase() === company || !company)
    );
    setWarn(dup ? `Sepertinya client "${dup.client_name}" sudah ada.` : '');
  }, [form.client_name, form.company_name, existingClients]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.client_name.trim()) {
      toast.error('Nama Client wajib diisi');
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post('/clients', form);
      toast.success('Client berhasil ditambahkan dan dipilih.');
      onCreated && onCreated(data);
      onOpenChange(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Gagal menambah client');
    } finally {
      setSaving(false);
    }
  };

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        {/* Use a slightly higher overlay so it stacks above the parent dialog */}
        <DialogPrimitive.Overlay className="fixed inset-0 z-[60] bg-black/60 backdrop-blur-sm" />
        <DialogPrimitive.Content
          data-testid="client-quick-create-dialog"
          className={cn(
            'fixed inset-0 z-[60] flex flex-col bg-card text-foreground outline-none',
            'sm:inset-auto sm:left-1/2 sm:top-1/2 sm:-translate-x-1/2 sm:-translate-y-1/2',
            'sm:w-[calc(100vw-2rem)] sm:max-w-lg sm:max-h-[92vh]',
            'sm:rounded-xl sm:border sm:border-border sm:shadow-2xl'
          )}
        >
          <form onSubmit={submit} className="flex flex-col h-full min-h-0">
            <div className="flex-none flex items-center justify-between gap-3 border-b border-border px-4 py-3 sm:px-5 sm:py-4">
              <div className="min-w-0">
                <DialogPrimitive.Title className="text-base sm:text-lg font-semibold leading-tight font-heading flex items-center gap-2">
                  <UserPlus className="h-5 w-5 text-primary" /> Tambah Client Baru
                </DialogPrimitive.Title>
                <DialogPrimitive.Description className="text-xs sm:text-sm text-muted-foreground mt-0.5">
                  Client baru akan otomatis terpilih pada form ini.
                </DialogPrimitive.Description>
              </div>
              <DialogPrimitive.Close className="h-9 w-9 rounded-md inline-flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-white/5 shrink-0" aria-label="Tutup">
                <X className="h-5 w-5" />
              </DialogPrimitive.Close>
            </div>
            <div className="flex-1 min-h-0 overflow-y-auto px-4 py-4 sm:px-5 space-y-3">
              <div className="space-y-1.5">
                <Label className="text-xs">Nama Client <span className="text-red-300">*</span></Label>
                <Input
                  autoFocus
                  className="h-11 md:h-10 text-base md:text-sm"
                  value={form.client_name}
                  onChange={(e) => setForm({ ...form, client_name: e.target.value })}
                  placeholder="Contoh: Andi Wijaya / PT Sejahtera"
                  data-testid="client-quick-name"
                />
                {warn && <p className="text-[11px] text-amber-300">{warn}</p>}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs">Nama Perusahaan</Label>
                  <Input className="h-11 md:h-10 text-base md:text-sm" value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Nama PIC</Label>
                  <Input className="h-11 md:h-10 text-base md:text-sm" value={form.pic_name} onChange={(e) => setForm({ ...form, pic_name: e.target.value })} />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">No WhatsApp</Label>
                  <Input inputMode="tel" className="h-11 md:h-10 text-base md:text-sm" value={form.whatsapp_number} onChange={(e) => setForm({ ...form, whatsapp_number: e.target.value })} placeholder="628xxxx" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">No Telepon</Label>
                  <Input inputMode="tel" className="h-11 md:h-10 text-base md:text-sm" value={form.phone_number} onChange={(e) => setForm({ ...form, phone_number: e.target.value })} />
                </div>
                <div className="space-y-1.5 sm:col-span-2">
                  <Label className="text-xs">Email</Label>
                  <Input type="email" className="h-11 md:h-10 text-base md:text-sm" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
                </div>
                <div className="space-y-1.5 sm:col-span-2">
                  <Label className="text-xs">Alamat</Label>
                  <Textarea rows={2} className="text-base md:text-sm" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
                </div>
                <div className="space-y-1.5 sm:col-span-2">
                  <Label className="text-xs">NPWP</Label>
                  <Input className="h-11 md:h-10 text-base md:text-sm font-mono-num" value={form.npwp} onChange={(e) => setForm({ ...form, npwp: e.target.value })} placeholder="00.000.000.0-000.000" />
                </div>
                <div className="space-y-1.5 sm:col-span-2">
                  <Label className="text-xs">Catatan</Label>
                  <Textarea rows={2} className="text-base md:text-sm" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
                </div>
              </div>
            </div>
            <div className="flex-none flex flex-col-reverse sm:flex-row sm:justify-end gap-2 border-t border-border px-4 py-3 sm:px-5 sm:py-4 pb-[calc(env(safe-area-inset-bottom,0)+12px)] sm:pb-4">
              <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} disabled={saving} className="h-11 md:h-9 w-full sm:w-auto">Batal</Button>
              <Button type="submit" disabled={saving} className="h-11 md:h-9 w-full sm:w-auto" data-testid="client-quick-save">
                {saving ? <><Loader2 className="h-4 w-4 animate-spin mr-2" />Menyimpan…</> : 'Simpan Client'}
              </Button>
            </div>
          </form>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
};
